import { useState } from 'react';
import { 
  Lightbulb, 
  X, 
  Send, 
  Loader2, 
  CheckCircle,
  Globe,
  Building2,
  Mail,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SuggestPlatformModal = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    platform_name: '',
    company_name: '',
    website_url: '',
    reason: '',
    contact_email: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit suggestion');
      }

      setIsSubmitted(true);
      setFormData({
        platform_name: '',
        company_name: '',
        website_url: '',
        reason: '',
        contact_email: ''
      });
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset state after animation
    setTimeout(() => {
      setIsSubmitted(false);
      setError(null);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm" className="gap-2">
            <Lightbulb className="w-4 h-4" />
            Suggest a platform
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        {isSubmitted ? (
          <div className="py-8 text-center">
            <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
            <DialogTitle className="text-xl mb-2">Thank you!</DialogTitle>
            <DialogDescription className="mb-6">
              Your suggestion has been submitted. We review all suggestions and may reach out if we need more information.
            </DialogDescription>
            <Button onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                Suggest a Platform
              </DialogTitle>
              <DialogDescription>
                Know an AI platform that should be on GoCompareAI? Let us know and we will review it.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="platform_name" className="flex items-center gap-2">
                  <Lightbulb className="w-3.5 h-3.5" />
                  Platform Name *
                </Label>
                <Input
                  id="platform_name"
                  name="platform_name"
                  placeholder="e.g., SuperAI Assistant"
                  value={formData.platform_name}
                  onChange={handleChange}
                  required
                  data-testid="suggest-platform-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5" />
                  Company Name *
                </Label>
                <Input
                  id="company_name"
                  name="company_name"
                  placeholder="e.g., SuperAI Inc."
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  data-testid="suggest-company-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_url" className="flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" />
                  Website URL
                </Label>
                <Input
                  id="website_url"
                  name="website_url"
                  type="url"
                  placeholder="https://example.com"
                  value={formData.website_url}
                  onChange={handleChange}
                  data-testid="suggest-website"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason" className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Why should we add it? *
                </Label>
                <Textarea
                  id="reason"
                  name="reason"
                  placeholder="Tell us what makes this platform noteworthy..."
                  value={formData.reason}
                  onChange={handleChange}
                  required
                  rows={3}
                  data-testid="suggest-reason"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_email" className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" />
                  Your Email *
                </Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.contact_email}
                  onChange={handleChange}
                  required
                  data-testid="suggest-email"
                />
                <p className="text-xs text-muted-foreground">
                  We may contact you if we need more details
                </p>
              </div>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 gap-2"
                  data-testid="suggest-submit-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
